import { z } from "zod";
import type { AccountKey, ContentMode, ImageSource, MaterialFormat } from "./types";

// Reusable account-key (brand slug) validator. Brands are dynamic/admin-managed,
// so this validates FORMAT only (lowercase slug); existence is checked by a DB
// lookup at the route (404 when the brand doesn't exist).
export const accountKeySchema = z
  .string()
  .min(2)
  .max(32)
  .regex(
    /^[a-z][a-z0-9-]*$/,
    "Slug brand harus huruf kecil/angka/strip dan diawali huruf",
  ) satisfies z.ZodType<AccountKey>;

// Reusable hero-image-source validator kept in sync with the ImageSource union.
export const imageSourceSchema = z.enum(["pexels", "library"]) satisfies z.ZodType<ImageSource>;

// Reusable content-mode validator kept in sync with the ContentMode union in types.ts.
// Used by draft/article list routes to filter by mode.
export const contentModeSchema = z.enum([
  "reguler",
  "special",
  "article",
  "custom",
]) satisfies z.ZodType<ContentMode>;

/**
 * POST /auth/login body.
 */
export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * POST /auth/change-password body. The user re-enters their current password
 * (the one they just logged in with) and chooses a new one — used for the
 * admin-reset forced change as well as a voluntary change. The new password
 * must differ from the current one.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8).max(200),
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: "Password baru harus berbeda dari password saat ini.",
    path: ["newPassword"],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * POST /accounts/:key/users/:userId/reset-password body. An admin sets a new
 * password for a brand login; the user is then forced to change it on next login.
 */
export const resetPasswordSchema = z.object({
  password: z.string().min(8).max(200),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/**
 * POST /accounts/:key/connections/:platform body for a "credentials" platform
 * (e.g. Bluesky). The expected keys depend on the platform's credentialFields;
 * the route validates that the required ones are present.
 */
export const connectSocialCredentialsSchema = z.object({
  credentials: z.record(z.string().min(1).max(1000)),
});
export type ConnectSocialCredentialsInput = z.infer<
  typeof connectSocialCredentialsSchema
>;

/**
 * POST /drafts/:id/publish-social body — the platforms to cross-post this draft
 * to (each must be a connected, publish-capable platform for the brand).
 */
export const publishSocialSchema = z.object({
  platforms: z.array(z.string().min(1).max(40)).min(1).max(10),
});
export type PublishSocialInput = z.infer<typeof publishSocialSchema>;

/**
 * POST /drafts/generate body. Three modes:
 *  - neither set → default single slot at D+7.
 *  - `targetDate` → single slot for that date.
 *  - `from` + `to` → one slot per day across the inclusive date range.
 * Dates are "YYYY-MM-DD" (or ISO datetime).
 */
export const generateDraftSchema = z.object({
  accountKey: accountKeySchema,
  targetDate: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /** Hero-image source override; falls back to the global default when omitted. */
  imageSource: imageSourceSchema.optional(),
});
export type GenerateDraftInput = z.infer<typeof generateDraftSchema>;

/**
 * POST /custom/generate body. The operator types a free-text `instruction` that
 * drives a Custom-mode draft. `imageQuery` (optional English keywords) overrides
 * the model's suggested stock-photo search; `targetDate` ("YYYY-MM-DD" or ISO)
 * defaults to D+7 when omitted.
 */
export const generateCustomSchema = z.object({
  accountKey: accountKeySchema,
  instruction: z.string().min(1),
  imageQuery: z.string().optional(),
  targetDate: z.string().optional(),
  /** Hero-image source override; falls back to the global default when omitted. */
  imageSource: imageSourceSchema.optional(),
});
export type GenerateCustomInput = z.infer<typeof generateCustomSchema>;

/**
 * PATCH /drafts/:id body. Every field is optional — only provided fields are
 * updated. `hashtags` fully replaces the existing array when present.
 */
export const updateDraftSchema = z.object({
  headline: z.string().optional(),
  subline: z.string().optional(),
  caption: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  suggestedTime: z.string().optional(),
  targetPublishAt: z.string().optional(),
});
export type UpdateDraftInput = z.infer<typeof updateDraftSchema>;

/**
 * POST /drafts/:id/revise body. The reviewer's free-text feedback that drives a
 * new AI revision (regenerating caption + image as a new version).
 */
export const reviseDraftSchema = z.object({
  feedback: z.string().min(1),
});
export type ReviseDraftInput = z.infer<typeof reviseDraftSchema>;

/**
 * PUT /settings/:key body. The setting value is stored verbatim as JSON, so it
 * is intentionally untyped here.
 */
export const updateSettingSchema = z.object({
  value: z.unknown(),
});
export type UpdateSettingInput = z.infer<typeof updateSettingSchema>;

/**
 * POST /highlights/:id/generate body — selects which highlight item in the
 * digest to turn into a Special Case draft.
 */
export const generateSpecialSchema = z.object({
  itemIndex: z.number().int().min(0),
  /** Hero-image source override; falls back to the global default when omitted. */
  imageSource: imageSourceSchema.optional(),
});
export type GenerateSpecialInput = z.infer<typeof generateSpecialSchema>;

// Reusable material-format validator kept in sync with the MaterialFormat union.
export const materialFormatSchema = z.enum([
  "flyer",
  "poster",
  "brosur",
  "trifold",
  "spanduk",
  "xbanner",
  "square",
]) satisfies z.ZodType<MaterialFormat>;

/**
 * POST /materials/generate body. Creates one offline marketing material (flyer/
 * banner/etc.) from a free-text `brief`. `withPhoto` toggles a background photo
 * (default true); when on, `imageSource` chooses Pexels vs the gallery and
 * `imageQuery` optionally overrides the model's suggested photo keywords. Choose
 * a preset `format`, or "custom" + `customWidthCm`/`customHeightCm` for a free size.
 */
export const materialGenerateSchema = z
  .object({
    accountKey: accountKeySchema,
    format: z.enum([
      "flyer",
      "poster",
      "brosur",
      "trifold",
      "spanduk",
      "xbanner",
      "square",
      "custom",
    ]),
    brief: z.string().min(1),
    withPhoto: z.boolean().optional(),
    imageSource: imageSourceSchema.optional(),
    imageQuery: z.string().optional(),
    /** Custom physical size in centimetres (required when format = "custom"). */
    customWidthCm: z.number().min(5).max(500).optional(),
    customHeightCm: z.number().min(5).max(500).optional(),
  })
  .refine(
    (v) =>
      v.format !== "custom" ||
      (v.customWidthCm != null && v.customHeightCm != null),
    {
      message: "Ukuran custom wajib diisi (lebar & tinggi dalam cm, 5–500).",
      path: ["customWidthCm"],
    },
  );
export type MaterialGenerateInput = z.infer<typeof materialGenerateSchema>;

/**
 * POST /videos/generate body (prototype). The operator gives a `topic`; the AI
 * writes a narrated educational-video script that's assembled from licensed
 * stock clips + TTS voice-over. `format` defaults to "vertical" (9:16).
 */
export const videoGenerateSchema = z.object({
  accountKey: accountKeySchema,
  topic: z.string().min(1),
  format: z.enum(["vertical", "square", "landscape"]).optional(),
});
export type VideoGenerateInput = z.infer<typeof videoGenerateSchema>;

/**
 * PATCH /media/:id body — edit a gallery asset's metadata. Every field is
 * optional; only provided fields are updated. `tags` fully replaces the array.
 */
export const updateMediaSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  active: z.boolean().optional(),
});
export type UpdateMediaInput = z.infer<typeof updateMediaSchema>;

/**
 * Body for account-scoped trigger routes (refresh highlights / research
 * articles). Identifies the target account.
 */
export const triggerAccountSchema = z.object({
  accountKey: accountKeySchema,
});
export type TriggerAccountInput = z.infer<typeof triggerAccountSchema>;

/**
 * PATCH /articles/:id body. Every field is optional — only provided fields are
 * updated. `seoMeta` is stored verbatim as JSON.
 */
export const updateArticleSchema = z.object({
  headline: z.string().optional(),
  bodyHtml: z.string().optional(),
  seoMeta: z.unknown().optional(),
  wpCategoryId: z.number().int().optional(),
});
export type UpdateArticleInput = z.infer<typeof updateArticleSchema>;

/**
 * POST /articles/:id/publish body. `status` chooses the WordPress post status;
 * `scheduledAt` (ISO datetime) is used when status is `future`.
 */
export const publishWpSchema = z.object({
  status: z.enum(["publish", "draft", "future"]).default("publish"),
  scheduledAt: z.string().optional(),
});
export type PublishWpInput = z.infer<typeof publishWpSchema>;

/**
 * PATCH /accounts/:key `wordpress` sub-object. Connects a WordPress site via an
 * application password. The credentials are encrypted at rest and never returned.
 */
export const connectWordpressSchema = z.object({
  siteUrl: z.string().url(),
  username: z.string().min(1),
  appPassword: z.string().min(1),
});
export type ConnectWordpressInput = z.infer<typeof connectWordpressSchema>;

/** Hex color like "#33BEBA" (used for a brand's accent color). */
const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Warna harus format hex, mis. #33BEBA");

/** A brand login (email + password). Used when creating a brand and to add more. */
const brandLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
});

/**
 * POST /accounts — admin creates a new brand AND (optionally) its first brand
 * login in one atomic step. `key` is the immutable slug; personalization is
 * editable later via PATCH.
 */
export const createBrandSchema = z.object({
  key: accountKeySchema,
  displayName: z.string().min(1).max(80),
  handle: z.string().max(40).optional(),
  accentColor: hexColorSchema.optional(),
  brandGuide: z.string().max(20000).optional(),
  /** Optional first login for the brand, created together with it. */
  login: brandLoginSchema.optional(),
});
export type CreateBrandInput = z.infer<typeof createBrandSchema>;

/**
 * PATCH /accounts/:key — admin updates a brand's personalization. Every field is
 * optional; `null` clears an optional field. The slug `key` is immutable.
 */
/** A single per-menu quota: `max` per `window` (max null ⇒ unlimited). */
const menuLimitSchema = z.object({
  max: z.number().int().min(0).max(100000).nullable(),
  window: z.enum(["day", "week", "month"]),
});

/** Per-brand usage quotas (each key optional ⇒ unlimited when absent). */
const brandLimitsSchema = z.object({
  reguler: menuLimitSchema.optional(),
  special: menuLimitSchema.optional(),
  article: menuLimitSchema.optional(),
  custom: menuLimitSchema.optional(),
  material: menuLimitSchema.optional(),
  video: menuLimitSchema.optional(),
  revise: menuLimitSchema.optional(),
});

export const updateBrandSchema = z.object({
  displayName: z.string().min(1).max(80).optional(),
  handle: z.string().max(40).nullable().optional(),
  accentColor: hexColorSchema.nullable().optional(),
  brandGuide: z.string().max(20000).nullable().optional(),
  /** Master toggle for the brand's daily auto-generation scheduler. */
  autoGenerate: z.boolean().optional(),
  /** Enabled content modules (menus); empty array ⇒ all enabled. */
  modules: z.array(z.string().min(1).max(40)).max(20).optional(),
  /** Per-menu usage quotas (generate/revise). */
  limits: brandLimitsSchema.optional(),
});
export type UpdateBrandInput = z.infer<typeof updateBrandSchema>;

/**
 * POST /accounts/:key/users — admin creates a brand-scoped login (role=brand,
 * bound to this brand). The password is argon2-hashed at rest.
 */
export const createBrandUserSchema = brandLoginSchema;
export type CreateBrandUserInput = z.infer<typeof createBrandUserSchema>;
